// ExecutionCenterPOC.js — Documentation Fonctionnelle : POC « Centre d'exécution » (module Campagne).
// Aperçu INTERACTIF de l'écran réel d'exécution des campagnes : barre de filtres (recherche, statut,
// campagne, vue), 4 KPI globaux, bandeau « Exécution en cours » (barre de progression + Pause/Arrêt +
// console), liste des exécutions (cartes / liste) et panneau de détails à 3 onglets (Vue d'ensemble,
// Résultats des tests, Statistiques). Les actions sont SIMULÉES localement via setTimeout (aucun réseau).
// Source de vérité : automata-studio-frontend [CPG][04]-ExecutionCenter.js + automata_campaign CampaignExecution.
import React, { useState } from 'react';
import {
  Play, Pause, Square, RefreshCw, CheckCircle, XCircle, Clock, Loader2, AlertTriangle,
  Activity, Target, BarChart2, Layers, Info, ArrowRight, Zap, Terminal, FileText, Shield,
  Settings, ListChecks, Wifi,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// ---- Référentiel des statuts d'exécution (ExecutionStatus du backend) -------
const STATUSES = {
  PENDING: { label: 'En attente', color: '#9CA3AF', icon: 'Clock' },
  INITIALIZING: { label: 'Initialisation', color: '#6366F1', icon: 'Loader2' },
  RUNNING: { label: 'En cours', color: '#3B82F6', icon: 'Play' },
  PAUSED: { label: 'En pause', color: '#F59E0B', icon: 'Pause' },
  COMPLETED_SUCCESS: { label: 'Réussi', color: '#10B981', icon: 'CheckCircle' },
  COMPLETED_FAILURE: { label: 'Échoué', color: '#EF4444', icon: 'XCircle' },
  CANCELLED: { label: 'Annulé', color: '#6B7280', icon: 'Square' },
  ERROR: { label: 'Erreur', color: '#9C27B0', icon: 'AlertTriangle' },
};
const statusOf = (s) => STATUSES[s] || { label: s, color: '#6B7280', icon: 'Circle' };

// ---- Données en dur : campagne exécutée + ses cas de test ---------------------
const CAMPAIGN = {
  name: 'Recette BGFI — Virements',
  environment: 'BGFI-Recette',
  executionId: 'EXEC-2026-0612-014',
  cases: [
    { id: 11, name: 'Connexion utilisateur', duration: 1200 },
    { id: 12, name: 'Saisie virement domestique', duration: 2600 },
    { id: 13, name: 'Validation plafond', duration: 1800 },
    { id: 14, name: 'Confirmation & reçu', duration: 1500 },
  ],
};

// ---- Liste des exécutions affichées dans le panneau gauche (données en dur) ---
const SAMPLE_EXECUTIONS = [
  { id: 1, executionId: CAMPAIGN.executionId, campaignName: CAMPAIGN.name, status: 'RUNNING', startTime: "Aujourd'hui 09:12", live: true },
  { id: 2, executionId: 'EXEC-2026-0612-013', campaignName: 'Non-régression — Comptes', status: 'COMPLETED_SUCCESS', startTime: "Aujourd'hui 08:40" },
  { id: 3, executionId: 'EXEC-2026-0611-027', campaignName: 'Smoke test — Connexion', status: 'COMPLETED_FAILURE', startTime: 'Hier 18:05' },
  { id: 4, executionId: 'EXEC-2026-0611-019', campaignName: 'Recette BGFI — Virements', status: 'CANCELLED', startTime: 'Hier 16:22' },
  { id: 5, executionId: 'EXEC-2026-0610-008', campaignName: 'Non-régression — Comptes', status: 'COMPLETED_SUCCESS', startTime: '10/06 11:30' },
];

const STATUS_FILTERS = [
  { label: 'Tous', value: '' },
  { label: 'En cours', value: 'RUNNING' },
  { label: 'Réussi', value: 'COMPLETED_SUCCESS' },
  { label: 'Échoué', value: 'COMPLETED_FAILURE' },
  { label: 'Annulé', value: 'CANCELLED' },
];

// ---- Petits sous-composants -------------------------------------------------
const StatusBadge = ({ status }) => {
  const s = statusOf(status);
  return <Badge color={s.color}><Icon name={s.icon} className="h-3 w-3" />{s.label}</Badge>;
};

const fmt = (ms) => {
  if (!ms || ms < 0) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  return `${Math.floor(sec / 60)}min ${(sec % 60).toString().padStart(2, '0')}s`;
};

// KPI global : valeur + icône dans un carré dégradé (réplique de renderLiveStats).
const KpiCard = ({ icon: IconCmp, label, value, gradient, textColor }) => (
  <div className="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-between">
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-3xl font-bold mt-1 ${textColor}`}>{value}</p>
    </div>
    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0`}>
      <IconCmp className="h-5 w-5 text-white" />
    </div>
  </div>
);

const InfoRow = ({ label, children }) => (
  <div>
    <p className="text-xs font-medium text-gray-400 dark:text-gray-500">{label}</p>
    <div className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">{children}</div>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-violet-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const ExecutionCenterPOC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewMode, setViewMode] = useState('card');     // 'card' | 'list'
  const [selectedId, setSelectedId] = useState(1);      // exécution sélectionnée (liste gauche)
  const [detailTab, setDetailTab] = useState('overview'); // 'overview' | 'results' | 'statistics'
  const [phase, setPhase] = useState('idle');           // 'idle' | 'running' | 'done'
  const [globalStatus, setGlobalStatus] = useState('PENDING');
  const [progress, setProgress] = useState(0);          // progression globale (%)
  const [runStates, setRunStates] = useState({});       // { [caseId]: { status, progress } }
  const [showConsole, setShowConsole] = useState(true);
  const [logs, setLogs] = useState([]);
  const [timers, setTimers] = useState([]);

  const cases = CAMPAIGN.cases;
  const totalCases = cases.length;
  const okCases = cases.filter((c) => runStates[c.id]?.status === 'COMPLETED_SUCCESS').length;
  const koCases = cases.filter((c) => runStates[c.id]?.status === 'COMPLETED_FAILURE').length;

  const selectedExec = SAMPLE_EXECUTIONS.find((e) => e.id === selectedId) || null;
  const filteredExecutions = SAMPLE_EXECUTIONS.filter((e) => {
    const matchSearch = `${e.campaignName} ${e.executionId}`.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || e.status === statusFilter;
    return matchSearch && matchStatus;
  });

  // KPI globaux dérivés de la liste + de l'exécution simulée.
  const kpiTotal = SAMPLE_EXECUTIONS.length;
  const kpiRunning = SAMPLE_EXECUTIONS.filter((e) => e.status === 'RUNNING').length + (phase === 'running' ? 0 : 0);
  const kpiCompleted = SAMPLE_EXECUTIONS.filter((e) => e.status === 'COMPLETED_SUCCESS').length;
  const kpiFailed = SAMPLE_EXECUTIONS.filter((e) => ['COMPLETED_FAILURE', 'ERROR'].includes(e.status)).length;

  const addLog = (level, source, message) =>
    setLogs((prev) => [...prev, { id: prev.length, time: new Date().toLocaleTimeString('fr-FR'), level, source, message }].slice(-60));

  const clearTimers = () => { timers.forEach((t) => window.clearTimeout(t)); setTimers([]); };

  // Lancer l'exécution SIMULÉE : chaque cas passe RUNNING puis SUCCESS/FAILURE en cascade.
  const startExecution = () => {
    if (phase === 'running') return;
    clearTimers();
    const init = {};
    cases.forEach((c) => { init[c.id] = { status: 'PENDING', progress: 0 }; });
    setRunStates(init);
    setPhase('running'); setGlobalStatus('INITIALIZING'); setProgress(0); setDetailTab('overview');
    setSelectedId(1); setShowConsole(true);
    setLogs([{ id: 0, time: new Date().toLocaleTimeString('fr-FR'), level: 'INFO', source: 'SYSTEM', message: `Exécution démarrée : ${CAMPAIGN.name}` }]);

    const newTimers = [];
    let cursor = 600;
    newTimers.push(window.setTimeout(() => { setGlobalStatus('RUNNING'); }, 400));

    cases.forEach((c, idx) => {
      const startAt = cursor;
      const dur = Math.max(700, Math.round(c.duration / 2)); // accéléré pour la démo
      newTimers.push(window.setTimeout(() => {
        setRunStates((prev) => ({ ...prev, [c.id]: { status: 'RUNNING', progress: 10 } }));
        addLog('INFO', 'TEST', `Démarrage test : ${c.name}`);
      }, startAt));
      newTimers.push(window.setTimeout(() => {
        setRunStates((prev) => ({ ...prev, [c.id]: { status: 'RUNNING', progress: 60 } }));
      }, startAt + dur * 0.5));
      newTimers.push(window.setTimeout(() => {
        const ok = idx !== 2; // le 3e cas échoue volontairement pour illustrer un KO
        setRunStates((prev) => ({ ...prev, [c.id]: { status: ok ? 'COMPLETED_SUCCESS' : 'COMPLETED_FAILURE', progress: 100 } }));
        setProgress(Math.round(((idx + 1) / totalCases) * 100));
        addLog(ok ? 'INFO' : 'WARN', 'TEST', `Test ${c.name} : ${ok ? 'Réussi' : 'Échoué'}`);
      }, startAt + dur));
      cursor = startAt + dur + 300;
    });

    // Verdict global
    newTimers.push(window.setTimeout(() => {
      const anyKo = true; // un cas échoue dans la démo
      setGlobalStatus(anyKo ? 'COMPLETED_FAILURE' : 'COMPLETED_SUCCESS');
      setProgress(100); setPhase('done'); setDetailTab('results');
      addLog(anyKo ? 'ERROR' : 'INFO', 'SYSTEM', `Exécution terminée : ${anyKo ? 'au moins un cas échoué' : 'tous les cas réussis'}`);
      setTimers([]);
    }, cursor));

    setTimers(newTimers);
  };

  const pauseExecution = () => {
    if (phase !== 'running') return;
    clearTimers();
    setGlobalStatus('PAUSED');
    addLog('WARN', 'SYSTEM', 'Exécution mise en pause');
  };

  const stopExecution = () => {
    clearTimers();
    setRunStates((prev) => {
      const next = { ...prev };
      cases.forEach((c) => {
        if (!['COMPLETED_SUCCESS', 'COMPLETED_FAILURE'].includes(next[c.id]?.status)) {
          next[c.id] = { status: 'CANCELLED', progress: next[c.id]?.progress || 0 };
        }
      });
      return next;
    });
    setGlobalStatus('CANCELLED'); setPhase('done');
    addLog('WARN', 'SYSTEM', 'Exécution arrêtée manuellement');
  };

  const resetExecution = () => {
    clearTimers();
    setPhase('idle'); setGlobalStatus('PENDING'); setProgress(0); setRunStates({}); setLogs([]);
    setDetailTab('overview');
  };

  const logColor = { INFO: 'text-emerald-400', WARN: 'text-amber-400', ERROR: 'text-red-400' };

  // KPI configuration (réplique de kpiCards du réel).
  const kpiCards = [
    { label: 'Total', value: kpiTotal, icon: Activity, gradient: 'from-violet-500 to-purple-600', textColor: 'text-violet-600 dark:text-violet-400' },
    { label: 'En cours', value: kpiRunning, icon: Zap, gradient: 'from-blue-500 to-cyan-600', textColor: 'text-blue-600 dark:text-blue-400' },
    { label: 'Terminées', value: kpiCompleted, icon: CheckCircle, gradient: 'from-emerald-500 to-green-600', textColor: 'text-emerald-600 dark:text-emerald-400' },
    { label: 'Échouées', value: kpiFailed, icon: XCircle, gradient: 'from-red-500 to-rose-600', textColor: 'text-red-600 dark:text-red-400' },
  ];

  const detailTabs = [
    { id: 'overview', label: "Vue d'ensemble" },
    { id: 'results', label: 'Résultats des tests' },
    { id: 'statistics', label: 'Statistiques' },
  ];

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Centre d'exécution</h1>
          <Badge color="#8B5CF6">Module Campagne</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Centre d'exécution</strong> (démo, actions simulées localement).
        </p>
      </div>

      {/* ===== Aperçu interactif de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête dégradé (violet → purple) */}
        <div className="px-5 py-5 bg-gradient-to-r from-violet-500 to-purple-700 text-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
                <Activity className="h-5 w-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg md:text-xl font-bold leading-tight">Centre d'Exécution</h2>
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-white/20 text-white">{SAMPLE_EXECUTIONS.length}</span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-500/20 text-emerald-100">
                    <Wifi className="h-3 w-3" /> Live
                  </span>
                </div>
                <p className="text-violet-200 text-sm">Supervision et gestion des exécutions de campagnes</p>
              </div>
            </div>
            <Button onClick={startExecution} className="bg-white/20 hover:bg-white/30 text-white">
              <Play className="h-4 w-4" /> Nouvelle exécution
            </Button>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Barre de filtres (recherche + statut + vue) */}
          <div className="p-3 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-3">
            <SearchBar value={search} onChange={setSearch} placeholder="Rechercher une exécution..." className="flex-1 min-w-[180px] max-w-xs" />
            <div className="flex items-center gap-1 flex-wrap">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-colors ${
                    statusFilter === f.value
                      ? 'bg-violet-500 text-white shadow-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 ml-auto">
              <div className="flex rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
                <button
                  onClick={() => setViewMode('card')}
                  className={`px-3 py-1.5 transition-colors ${viewMode === 'card' ? 'bg-violet-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  title="Vue cartes"
                >
                  <BarChart2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 transition-colors ${viewMode === 'list' ? 'bg-violet-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                  title="Vue liste"
                >
                  <Layers className="h-4 w-4" />
                </button>
              </div>
              <button onClick={resetExecution} className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" title="Rafraîchir">
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* KPI globaux (4 cartes) */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {kpiCards.map((k) => (
              <KpiCard key={k.label} icon={k.icon} label={k.label} value={k.value} gradient={k.gradient} textColor={k.textColor} />
            ))}
          </div>

          {/* Bandeau « Exécution en cours » (visible pendant/à la fin de la démo) */}
          {phase !== 'idle' && (
            <div className="p-5 rounded-2xl bg-white dark:bg-gray-800 border-2 border-blue-400/50 dark:border-blue-500/30">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  <span className="font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                    <Activity className="h-4 w-4 text-blue-500" /> Exécution en cours
                  </span>
                  <StatusBadge status={globalStatus} />
                </div>
                <div className="flex items-center gap-2">
                  {phase === 'running' && globalStatus !== 'PAUSED' ? (
                    <Button variant="outline" size="sm" onClick={pauseExecution}><Pause className="h-4 w-4" /> Pause</Button>
                  ) : phase === 'running' ? (
                    <Button size="sm" onClick={startExecution} className="bg-emerald-500 hover:bg-emerald-600 text-white"><Play className="h-4 w-4" /> Reprendre</Button>
                  ) : null}
                  {phase === 'running' && <Button variant="danger" size="sm" onClick={stopExecution}><Square className="h-4 w-4" /> Arrêter</Button>}
                  {phase === 'done' && <Button variant="outline" size="sm" onClick={resetExecution}><RefreshCw className="h-4 w-4" /> Réinitialiser</Button>}
                  <button onClick={() => setShowConsole((v) => !v)} className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <Terminal className="h-3.5 w-3.5" /> {showConsole ? 'Masquer console' : 'Afficher console'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Campagne : <span className="text-gray-700 dark:text-gray-200 font-medium">{CAMPAIGN.name}</span></p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Environnement : <span className="text-gray-700 dark:text-gray-200">{CAMPAIGN.environment}</span></p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                  </div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{progress}%</span>
                </div>
              </div>

              {/* Console inline */}
              {showConsole && (
                <div className="mt-4 rounded-xl bg-gray-900 dark:bg-black/60 border border-gray-800 p-3 font-mono text-xs max-h-48 overflow-y-auto">
                  {logs.length === 0 ? (
                    <span className="text-gray-500 italic">En attente de logs…</span>
                  ) : logs.map((l) => (
                    <div key={l.id} className="py-0.5 flex gap-2">
                      <span className="text-gray-500 shrink-0">[{l.time}]</span>
                      <span className="text-gray-600 shrink-0">[{l.source}]</span>
                      <span className={`${logColor[l.level] || 'text-gray-300'} break-all`}>{l.message}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Vue principale : liste des exécutions (gauche) + détails (droite) */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
            {/* Panneau gauche : liste des exécutions */}
            <div className="lg:col-span-4">
              <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <span className="font-semibold text-gray-700 dark:text-gray-200">
                    Exécutions
                    <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">{filteredExecutions.length}</span>
                  </span>
                </div>
                <div className="p-3 space-y-2 max-h-[460px] overflow-y-auto">
                  {filteredExecutions.length === 0 ? (
                    <EmptyState icon={Target} title="Aucune exécution trouvée" message="Modifiez votre recherche ou vos filtres." />
                  ) : viewMode === 'card' ? (
                    filteredExecutions.map((e) => {
                      const on = selectedId === e.id;
                      const s = statusOf(e.status);
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedId(e.id)}
                          className={`w-full text-left p-3 rounded-xl border-2 transition-all ${on ? 'border-violet-400 dark:border-violet-500 bg-violet-50 dark:bg-violet-500/10' : 'border-transparent hover:border-gray-200 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'}`}
                        >
                          <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{e.campaignName}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 font-mono">ID : {e.executionId}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            <StatusBadge status={e.status} />
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                              <Clock className="h-3 w-3" /> {e.startTime}
                            </span>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    filteredExecutions.map((e) => {
                      const on = selectedId === e.id;
                      const s = statusOf(e.status);
                      return (
                        <button
                          key={e.id}
                          onClick={() => setSelectedId(e.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-all border-l-4 ${on ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-500' : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/30'}`}
                        >
                          <span className="w-9 h-9 rounded-xl flex items-center justify-center text-white shrink-0" style={{ backgroundColor: s.color }}>
                            <Icon name={s.icon} className="h-4 w-4" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">{e.campaignName}</p>
                            <div className="mt-0.5"><StatusBadge status={e.status} /></div>
                          </div>
                          <div className="text-right shrink-0">
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{e.executionId}</p>
                            <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{e.startTime}</p>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Panneau droit : détails à 3 onglets */}
            <div className="lg:col-span-8">
              <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 overflow-hidden">
                {!selectedExec ? (
                  <EmptyState icon={Target} title="Sélectionnez une exécution" message="Choisissez une exécution dans la liste pour voir les détails, résultats et statistiques." />
                ) : (
                  <>
                    <div className="px-4 pt-3">
                      <Tabs tabs={detailTabs} active={detailTab} onChange={setDetailTab} />
                    </div>
                    <div className="p-4">
                      {/* --- Onglet Vue d'ensemble --- */}
                      {detailTab === 'overview' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><Info className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Informations générales</span>
                            </div>
                            <div className="space-y-3">
                              <InfoRow label="Campagne">{selectedExec.campaignName}</InfoRow>
                              <InfoRow label="ID d'exécution"><span className="font-mono text-xs">{selectedExec.executionId}</span></InfoRow>
                              <InfoRow label="Statut"><StatusBadge status={selectedExec.id === 1 ? globalStatus : selectedExec.status} /></InfoRow>
                              <InfoRow label="Progression">
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-purple-600 transition-all duration-500" style={{ width: `${selectedExec.id === 1 ? progress : 100}%` }} />
                                  </div>
                                  <span className="text-xs font-medium">{selectedExec.id === 1 ? progress : 100}%</span>
                                </div>
                              </InfoRow>
                            </div>
                          </div>

                          <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center"><Clock className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Chronologie</span>
                            </div>
                            <div className="space-y-3">
                              <InfoRow label="Démarré le">{selectedExec.startTime}</InfoRow>
                              <InfoRow label="Durée">{phase === 'done' || selectedExec.id !== 1 ? '6min 30s' : 'En cours...'}</InfoRow>
                              <InfoRow label="Environnement">{CAMPAIGN.environment}</InfoRow>
                            </div>
                          </div>

                          <div className="md:col-span-2 p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center"><BarChart2 className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Résumé des tests</span>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {[
                                { label: 'Total', value: totalCases, color: 'text-violet-600 dark:text-violet-400' },
                                { label: 'Réussis', value: okCases, color: 'text-emerald-600 dark:text-emerald-400' },
                                { label: 'Échoués', value: koCases, color: 'text-red-600 dark:text-red-400' },
                                { label: 'Ignorés', value: 0, color: 'text-amber-600 dark:text-amber-400' },
                              ].map((st) => (
                                <div key={st.label} className="text-center">
                                  <p className={`text-2xl md:text-3xl font-bold ${st.color}`}>{st.value}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{st.label}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="md:col-span-2 p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center"><Settings className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Paramètres d'exécution</span>
                            </div>
                            <div className="space-y-1.5 text-sm">
                              {[['Priorité', 'Normale'], ['Timeout', '3600 s'], ['Exécution parallèle', 'Non'], ["Arrêt sur échec", 'Oui']].map(([k, v]) => (
                                <div key={k} className="flex justify-between"><span className="text-gray-500 dark:text-gray-400">{k} :</span><span className="text-gray-800 dark:text-gray-200">{v}</span></div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* --- Onglet Résultats des tests --- */}
                      {detailTab === 'results' && (
                        phase === 'idle' ? (
                          <EmptyState icon={FileText} title="Aucun résultat de test" message="Lancez l'exécution pour suivre les cas de test en temps réel." />
                        ) : (
                          <div>
                            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Résultats de test — {CAMPAIGN.name}</span>
                              <div className="flex items-center gap-2">
                                {okCases > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"><CheckCircle className="h-3 w-3" /> {okCases}</span>}
                                {koCases > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"><XCircle className="h-3 w-3" /> {koCases}</span>}
                                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">{totalCases} tests</span>
                              </div>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                                  <tr>{['Cas de test', 'Statut', 'Durée', 'Progression'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                  {cases.map((c) => {
                                    const rs = runStates[c.id] || { status: 'PENDING', progress: 0 };
                                    const s = statusOf(rs.status);
                                    const running = rs.status === 'RUNNING';
                                    return (
                                      <tr key={c.id} className="text-gray-700 dark:text-gray-200">
                                        <td className="px-4 py-2 font-medium">
                                          <span className="flex items-center gap-2">
                                            {running ? <Loader2 className="h-4 w-4 animate-spin text-blue-500" /> : <Icon name={s.icon} className="h-4 w-4" style={{ color: s.color }} />}
                                            {c.name}
                                          </span>
                                        </td>
                                        <td className="px-4 py-2"><StatusBadge status={rs.status} /></td>
                                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">{['COMPLETED_SUCCESS', 'COMPLETED_FAILURE'].includes(rs.status) ? fmt(c.duration) : running ? '…' : '—'}</td>
                                        <td className="px-4 py-2 w-40">
                                          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${rs.progress}%`, backgroundColor: s.color }} />
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )
                      )}

                      {/* --- Onglet Statistiques --- */}
                      {detailTab === 'statistics' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center"><BarChart2 className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Répartition des résultats</span>
                            </div>
                            <div className="space-y-3">
                              {[
                                { name: 'Réussis', value: okCases, color: '#10B981' },
                                { name: 'Échoués', value: koCases, color: '#EF4444' },
                                { name: 'Ignorés', value: 0, color: '#F59E0B' },
                              ].map((d) => (
                                <div key={d.name}>
                                  <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300 mb-1">
                                    <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} /> {d.name}</span>
                                    <span>{d.value}</span>
                                  </div>
                                  <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${totalCases ? (d.value / totalCases) * 100 : 0}%`, backgroundColor: d.color }} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center"><Activity className="h-4 w-4 text-white" /></div>
                              <span className="font-semibold text-gray-700 dark:text-gray-200">Métriques détaillées</span>
                            </div>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Taux de réussite</p>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-2.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-green-500 transition-all duration-700" style={{ width: `${totalCases ? Math.round((okCases / totalCases) * 100) : 0}%` }} />
                                  </div>
                                  <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{totalCases ? Math.round((okCases / totalCases) * 100) : 0}%</span>
                                </div>
                              </div>
                              <div className="h-px bg-gray-200 dark:bg-gray-700" />
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Durée moyenne par test</p>
                                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-0.5">{fmt(Math.round(cases.reduce((a, c) => a + c.duration, 0) / totalCases))}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Temps total d'exécution</p>
                                <p className="text-xl font-bold text-gray-800 dark:text-gray-100 mt-0.5">{fmt(cases.reduce((a, c) => a + c.duration, 0))}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Play className="h-5 w-5 text-violet-500" />Lancer une exécution</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouvelle exécution »</strong> dans l'en-tête. <button onClick={startExecution} className="text-violet-600 dark:text-violet-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Vérifier l'<strong>environnement cible</strong> et le nombre de cas de test.</GuideStep>
              <GuideStep n={3}>Suivre le bandeau <strong>« Exécution en cours »</strong> qui apparaît en haut.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Activity className="h-5 w-5 text-violet-500" />Suivre la progression</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La <strong>barre globale</strong> et les <strong>KPI</strong> (Total / En cours / Terminées / Échouées) se mettent à jour.</GuideStep>
              <GuideStep n={2}>Ouvrir l'onglet <strong>Résultats des tests</strong> pour le détail par cas. <button onClick={() => setDetailTab('results')} className="text-violet-600 dark:text-violet-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={3}>Consulter la <strong>console</strong> du bandeau pour les événements.</GuideStep>
              <GuideStep n={4}>Besoin de stopper ? <strong>« Arrêter »</strong> bascule les cas en attente en <strong>Annulé</strong>. <button onClick={stopExecution} className="text-violet-600 dark:text-violet-400 underline">Arrêter</button></GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><FileText className="h-5 w-5 text-violet-500" />Lire le résultat</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>En fin d'exécution, le statut devient <span className="text-emerald-600">Réussi</span> ou <span className="text-red-600">Échoué</span>.</GuideStep>
              <GuideStep n={2}>Comparer les compteurs <strong>Réussis</strong> / <strong>Échoués</strong> dans <strong>Vue d'ensemble</strong>.</GuideStep>
              <GuideStep n={3}>Analyser le taux de réussite via l'onglet <strong>Statistiques</strong>. <button onClick={() => setDetailTab('statistics')} className="text-violet-600 dark:text-violet-400 underline">Voir</button></GuideStep>
              <GuideStep n={4}>Cliquer sur <strong>« Réinitialiser »</strong> pour relancer. <button onClick={resetExecution} className="text-violet-600 dark:text-violet-400 underline">Réinitialiser</button></GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence : statuts d'exécution ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><BarChart2 className="h-5 w-5 text-violet-500" />Statuts d'exécution</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Statut', 'Libellé', 'Signification'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['PENDING', "L'exécution est créée mais n'a pas encore démarré."],
                ['INITIALIZING', "Préparation de l'environnement et chargement des cas de test."],
                ['RUNNING', 'Les cas de test sont rejoués séquentiellement sur la cible.'],
                ['PAUSED', "L'exécution est suspendue ; elle peut être reprise."],
                ['COMPLETED_SUCCESS', 'Tous les cas de test ont réussi.'],
                ['COMPLETED_FAILURE', "Au moins un cas de test a échoué."],
                ['CANCELLED', "L'exécution a été arrêtée manuellement par l'utilisateur."],
                ['ERROR', "Une erreur technique a interrompu l'exécution (agent, connexion…)."],
              ].map((r) => (
                <tr key={r[0]} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2"><StatusBadge status={r[0]} /></td>
                  <td className="px-4 py-2 font-medium">{statusOf(r[0]).label}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[1]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      {/* ===== Règles de gestion + Endpoints API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Shield className="h-5 w-5 text-violet-500" />Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Seules les campagnes <strong>ACTIVE / READY / SCHEDULED / IN_PROGRESS</strong> peuvent être lancées.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />L'exécution rejoue les cas <strong>en séquence</strong> ; la progression remonte en <strong>temps réel</strong> via WebSocket.</li>
            <li className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />Avec <strong>« Arrêt sur échec »</strong>, le premier cas KO met fin à l'exécution.</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un <strong>arrêt manuel</strong> bascule les cas non terminés en statut <strong>Annulé</strong>.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><ListChecks className="h-5 w-5 text-violet-500" />Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['POST', '#3B82F6', '/api/automata/executions/campaigns/{id}/start'],
              ['POST', '#F59E0B', '/api/automata/executions/{id}/pause'],
              ['POST', '#10B981', '/api/automata/executions/{id}/resume'],
              ['POST', '#EF4444', '/api/automata/executions/{id}/stop'],
              ['GET', '#10B981', '/api/automata/executions'],
              ['GET', '#10B981', '/api/automata/executions/{id}/test-results'],
              ['GET', '#10B981', '/api/automata/executions/{id}/statistics'],
              ['WS', '#8B5CF6', '/api/automata/campaign-execution/ws'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_campaign / CampaignExecutionController + WebSocket temps réel</p>
        </CardContent></Card>
      </div>

      {/* Pied de page informatif */}
      <div className="flex items-start gap-2 text-xs text-gray-400 dark:text-gray-500">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <p>Démo : un cas sur quatre échoue volontairement pour illustrer un verdict <strong>Échoué</strong>. Les durées sont accélérées et aucune donnée réelle n'est sollicitée.</p>
      </div>
    </div>
  );
};

export default ExecutionCenterPOC;
